import React from 'react';
import { Container, Box, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';

const Lb1Page: React.FC = () => {
  const navigate = useNavigate();
  const { language, t, setLanguage } = useLocale();
  const isHebrew = language === 'he';

  const handleAssessmentClick = () => {
    navigate('/assessment/360');
  };

  // Set locale from query string if present (only on mount)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam && (langParam === 'he' || langParam === 'en')) {
      setLanguage(langParam);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const hebrew = language === 'he';
    const title = hebrew
      ? 'מבדק דיוק עסקי | אבחון לעסקים וצמיחה - TurnKeyOS.AI'
      : 'Business Precision Assessment | Diagnostic for Growth - TurnKeyOS.AI';
    const description = hebrew
      ? 'גלה איפה אתה מבזבז זמן, שורף כסף ומפספס פוטנציאל עסקי. מבדק ייחודי למנכ"לים ובעלי עסקים, כולל המלצות לצמיחה מוכחת.'
      : 'Discover where you waste time, burn money, and miss business potential. Unique assessment for CEOs and business owners, with proven growth recommendations.';
    document.title = title;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta') as HTMLMetaElement;
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);
  }, [language]);
  // Labels for black name boxes: take only the name portion before any colon
  const [labelTestimonial1, tagTestimonial1] = React.useMemo(() => {
    const raw = t('homepage.testimonial1.name') || '';
    const seps = [':', ' – ', ' — ', ' - ', '|', '—', '–', '-'];
    for (const sep of seps) {
      if (raw.includes(sep)) {
        const [left, right] = raw.split(sep);
        return [left.trim(), (right || '').trim()];
      }
    }
    return [raw.trim(), ''];
  }, [t]);
  const [labelTestimonial2, tagTestimonial2] = React.useMemo(() => {
    const raw = t('homepage.testimonial2.name') || '';
    const seps = [':', ' – ', ' — ', ' - ', '|', '—', '–', '-'];
    for (const sep of seps) {
      if (raw.includes(sep)) {
        const [left, right] = raw.split(sep);
        return [left.trim(), (right || '').trim()];
      }
    }
    return [raw.trim(), ''];
  }, [t]);

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', width: '100%', py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ backgroundColor: 'white', border: '2px solid #cccccc', borderRadius: 2, overflow: 'hidden', maxWidth: 1200, mx: 'auto' }}>
        {/* Blue Bar at Top - outside container for full width */}
        <Box
          sx={{
            width: '100%',
            height: { xs: '16px', sm: '20px', md: '24px' },
            backgroundColor: '#5B9BD5',
            mb: { xs: 4, sm: 6, md: 8 },
          }}
        />
        <Container maxWidth="md" sx={{ direction: isHebrew ? 'rtl' : 'ltr', px: { xs: 0.5, sm: 2, md: 0 } }}>

      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 2, sm: 4 },
          px: { xs: 0.5, sm: 2 },
        }}
      >
        <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
          <img 
            src={isHebrew ? "/logo.png" : "/logo-english.png"} 
            alt="Logo" 
            style={{ width: '100%', maxWidth: 180, height: 'auto', minWidth: 90 }}
          />
        </Box>
        
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#003366',
            mb: 1,
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
          }}
        >
          {t('homepage.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
            color: '#000',
            textAlign: 'center',
            mb: 3,
            fontWeight: 400,
          }}
            >
              {t('homepage.subtitle')}
        </Typography>
        <Box sx={{ mb: { xs: 5, sm: 6 }, textAlign: 'center', position: 'relative', pt: 3 }}>
          {/* Overlay pill like in the design (blue instead of red) */}
          <Box
            onClick={handleAssessmentClick}
            sx={{
              cursor: 'pointer',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#5B9BD5',
              color: 'white',
              px: { xs: 1.2, sm: 3, md: 4 },
              py: { xs: 0.6, sm: 1, md: 1.2 },
              borderRadius: { xs: 3, sm: 4, md: 5 },
              fontWeight: 800,
              fontSize: { xs: '0.6rem', sm: '1rem', md: '1.15rem' },
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              zIndex: 10,
              overflow: 'visible',
              maxWidth: { xs: '98%', sm: '90%', md: 640 },
              minHeight: { xs: 32, sm: 40 },
              width: 'fit-content',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              component="span"
              sx={{ fontWeight: 800, fontSize: 'inherit', color: 'inherit', width: '100%', textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: t('homepage.intro') }}
            />
          </Box>
          <Box sx={{ mt: { xs: 4, sm: 5, md: 6 }, display: 'flex', justifyContent: 'center' }}>
            <img
              src="/asses1.jpg"
              alt="Performance Graph"
              style={{ width: '100%', maxWidth: '900px', minWidth: 0, height: 'auto', borderRadius: 12, boxShadow: '0 2px 12px rgba(91,155,213,0.10)' }}
            />
          </Box>
        </Box>

        {/* Main Content with Testimonials */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, md: 4 },
            alignItems: 'start',
          }}
        >
          {/* Left Side - Informative Text */}
          <Box sx={{ textAlign: isHebrew ? 'right' : 'left', px: { xs: 0.5, sm: 1, md: 0 }, order: { xs: 2, md: 1 } }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.9 }}>
              {t('homepage.description')} <b>{t('homepage.description2')}</b> {t('homepage.analysis')}
            </Typography>            
          </Box>

          {/* Right Side - Testimonial Box 1 */}
          <Box sx={{ position: 'relative', mb: { xs: 14, md: 12 }, order: { xs: 1, md: 2 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 4 },
                border: '3px solid #5B9BD5',
                borderRadius: '16px',
                position: 'relative',
                textAlign: isHebrew ? 'right' : 'left',
                backgroundColor: 'white',
                overflow: 'visible',
                // Tail with outlined border (blue) and white fill - BIGGER
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-32px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '32px solid transparent',
                  borderRight: '32px solid transparent',
                  borderTop: '32px solid #5B9BD5',
                  zIndex: 1,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-28px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '28px solid transparent',
                  borderRight: '28px solid transparent',
                  borderTop: '28px solid #ffffff',
                  zIndex: 2,
                },
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: { xs: '2.4rem', md: '3rem' },
                  color: '#003366',
                  backgroundColor: 'white',
                  width: '80px',
                  textAlign: 'center',
                  mb: 1,
                  lineHeight: 1,
                  fontWeight: 700,
                  mt: -6,
                  letterSpacing: '-1px',
                }}
              >
                ❝
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  color: '#000',
                  fontWeight: 500,
                }}
              >
                <b>{t('homepage.testimonial1.quote').replace('"', '').replace('"', '')}</b> {t('homepage.testimonial1.quote2').replace('"', '').replace('"', '')}
              </Typography>              
              {/* Name inside card */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: isHebrew ? 'flex-end' : 'flex-start' }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{labelTestimonial1}</Typography>
                {tagTestimonial1 && (
                  <Box
                    sx={{
                      background: '#5B9BD5',
                      color: '#fff',
                      borderRadius: 999,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                      lineHeight: 1,
                    }}
                  >
                    {tagTestimonial1}
                  </Box>
                )}
              </Box>
            </Paper>
            {/* Logo positioned parallel to the tail */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: '-50px', sm: '-56px', md: '-54px' },
                left: '50%',
                transform: {
                  xs: isHebrew ? 'translateX(0px)' : 'translateX(-60px)',
                  sm: isHebrew ? 'translateX(30px)' : 'translateX(-110px)',
                  md: isHebrew ? 'translateX(30px)' : 'translateX(-110px)',
                },
                zIndex: 3,
              }}
            >
              <img
                src="/stern-logo.png"
                alt={isHebrew ? 'אמנון שטרן' : 'Amnon Stern'}
                style={{ width: '110px', maxWidth: '28vw', height: 'auto', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>
        </Box>

        {/* CTA Button 1 */}
        <Button
          variant="contained"
          size="large"
          onClick={handleAssessmentClick}
          sx={{
            py: { xs: 0.6, sm: 1, md: 1.2 },
            px: { xs: 1.2, sm: 3, md: 4 },
            fontSize: { xs: '0.75rem', sm: '1rem', md: '1.15rem' },
            borderRadius: { xs: 3, sm: 4, md: 5 },
            backgroundColor: '#5B9BD5',
            color: 'white',
            fontWeight: 700,
            mb: { xs: 6, md: 8 },
            width: '100%',
            maxWidth: 640,
            minHeight: { xs: 32, sm: 40 },
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(91, 155, 213, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#4A8AC4',
              boxShadow: '0 6px 20px rgba(91, 155, 213, 0.4)',
            }
          }}
        >
          {t('homepage.button')}
        </Button>

        {/* Bottom Section with Second Testimonial */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 2, sm: 3, md: 4 },
            mb: 4,
            alignItems: 'start',
          }}
        >
          {/* Left Side - Testimonial Box 2 */}
          <Box sx={{ position: 'relative', mb: { xs: 14, md: 12 }, order: { xs: 2, md: 1 } }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 4 },
                border: '3px solid #5B9BD5',
                borderRadius: '16px',
                position: 'relative',
                textAlign: isHebrew ? 'right' : 'left',
                backgroundColor: 'white',
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-32px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '32px solid transparent',
                  borderRight: '32px solid transparent',
                  borderTop: '32px solid #5B9BD5',
                  zIndex: 1,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-28px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '28px solid transparent',
                  borderRight: '28px solid transparent',
                  borderTop: '28px solid #ffffff',
                  zIndex: 2,
                },
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: { xs: '2.6rem', md: '3.2rem' },
                  color: 'black',
                  backgroundColor: 'white',
                  width: '80px',
                  textAlign: 'center',
                  mb: 1,
                  mt: -6,                  
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                ❝
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  color: '#000',
                  fontWeight: 500,
                }}
              >
                <b>{t('homepage.testimonial2.quote').replace('"', '').replace('"', '')}</b> {t('homepage.testimonial2.quote2').replace('"', '').replace('"', '')} {t('homepage.testimonial2.quote3').replace('"', '').replace('"', '')}
              </Typography>
              {/* Name inside card */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: isHebrew ? 'flex-end' : 'flex-start' }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{labelTestimonial2}</Typography>
                {tagTestimonial2 && (
                  <Box
                    sx={{
                      background: '#5B9BD5',
                      color: '#fff',
                      borderRadius: 999,
                      padding: '4px 8px',
                      fontSize: '0.7rem',
                      lineHeight: 1,
                    }}
                  >
                    {tagTestimonial2}
                  </Box>
                )}
              </Box>
            </Paper>
            {/* Logo positioned parallel to the tail */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: '-50px', sm: '-56px', md: '-54px' },
                left: '50%',
                transform: {
                  xs: isHebrew ? 'translateX(0px)' : 'translateX(-70px)',
                  sm: isHebrew ? 'translateX(30px)' : 'translateX(-130px)',
                  md: isHebrew ? 'translateX(30px)' : 'translateX(-130px)',
                },
                zIndex: 3,
              }}
            >
              <img
                src="/ronenchen-logo.png"
                alt={isHebrew ? 'רונן חן' : 'Ronen Chen'}
                style={{ width: '120px', maxWidth: '32vw', height: 'auto', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>

          {/* Right Side - Text Content */}
          <Box sx={{ textAlign: isHebrew ? 'right' : 'left', order: { xs: 1, md: 2 }, px: { xs: 0.5, sm: 1, md: 0 } }}>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              {t('homepage.proof')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 700 }}>
              {t('homepage.invitation')}
            </Typography>
          </Box>
        </Box>

        {/* Bottom List styled like benefit bullets */}
  <Box sx={{ textAlign: 'center', mb: 4, mt: 8, px: { xs: 1, sm: 2, md: 0 } }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t('homepage.comment')}
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              maxWidth: 720,
              mx: 'auto',
              textAlign: isHebrew ? 'right' : 'left',
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
            }}
          >
            <li style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>• </span>
              <span>{t('homepage.benefit1')}</span>
            </li>
            <li style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>• </span>
              <span>{t('homepage.benefit2')}</span>
            </li>
            <li style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>• </span>
              <span>{t('homepage.benefit3')}</span>
            </li>
          </Box>
        </Box>

        {/* CTA Button 2 */}
        <Button
          variant="contained"
          size="large"
          onClick={handleAssessmentClick}
          sx={{
            py: { xs: 0.6, sm: 1, md: 1.2 },
            px: { xs: 1.2, sm: 3, md: 4 },
            fontSize: { xs: '0.75rem', sm: '1rem', md: '1.15rem' },
            borderRadius: { xs: 3, sm: 4, md: 5 },
            backgroundColor: '#5B9BD5',
            color: 'white',
            fontWeight: 700,
            mb: { xs: 3, md: 4 },
            width: '100%',
            maxWidth: 640,
            minHeight: { xs: 32, sm: 40 },
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(91, 155, 213, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#4A8AC4',
              boxShadow: '0 6px 20px rgba(91, 155, 213, 0.4)',
            }
          }}
        >
          {t('homepage.cta')}
        </Button>
      </Box>
    </Container>
      </Box>
    </Box>
  );
};

export default Lb1Page;
